/**
 * SongShake multistreaming (ENG-763, backend ENG-757). Streamers send every
 * show to SongShake as well as YouTube/Twitch; SongShake records that feed
 * and cuts each requested song's clip from it. With the server's
 * `requireFeed` on, Go Live is refused until SongShake is receiving it.
 *
 * - `GET /bands/{id}/multistream/status` (any member): `{ configured,
 *   requireFeed, hasChannel, feed: { live, source, since? } }`, no secrets.
 * - `GET /bands/{id}/multistream/stream-key` (performers of the band):
 *   `{ ingestUrl, streamKey, feed }`; the first call creates the channel.
 * - `POST /bands/{id}/multistream/stream-key/rotate`: the same, with a new key.
 *
 * The stream key is a secret: it lives only in component state, is never
 * logged, stored or sent anywhere else.
 */

/** The `{ configured, requireFeed, hasChannel, feed }` status, or null for anything else. */
export const normalizeMultistreamStatus = (data) => {
  if (!data || typeof data !== 'object') return null;
  const feed = data.feed && typeof data.feed === 'object' ? data.feed : {};
  return {
    configured: data.configured === true,
    requireFeed: data.requireFeed === true,
    hasChannel: data.hasChannel === true,
    feed: {
      live: feed.live === true,
      source: typeof feed.source === 'string' ? feed.source : 'none',
      since: typeof feed.since === 'string' ? feed.since : null,
    },
  };
};

/** Whether Go Live has to wait for the feed: the server requires it and it isn't live. */
export const isFeedGated = (status) =>
  Boolean(status?.configured && status.requireFeed && !status.feed.live);

/**
 * Whether a live show has lost (or never had) its SongShake feed, so songs
 * played meanwhile get no replay clip. Only a band with a channel can be
 * missing one; without a channel there is no feed to have dropped.
 */
export const isFeedDown = (status) =>
  Boolean(status?.configured && status.hasChannel && !status.feed.live);

/** Go Live's refusal codes (ENG-757). */
export const FEED_MISSING_CODE = 'songshake_feed_missing';
export const FEED_CHECK_UNAVAILABLE_CODE = 'service_unavailable';

/** Catalog key for a failed stream-key read or rotate, by the envelope's code. */
export const streamKeyErrorKey = (err) => {
  switch (err?.code) {
    case 'performer_role_required':
    case 'band_access_denied':
      return 'multistream.errors.performersOnly';
    case 'multistream_not_configured':
      return 'multistream.errors.notConfigured';
    case 'multistream_provider_error':
      return 'multistream.errors.provider';
    case 'band_not_found':
      return 'multistream.errors.bandNotFound';
    default:
      return 'multistream.errors.generic';
  }
};

/** Whether a stream-key failure is worth retrying (the others won't change on their own). */
export const isRetryableStreamKeyError = (err) =>
  !['performer_role_required', 'band_access_denied', 'multistream_not_configured', 'band_not_found'].includes(err?.code);
